// Banner unten rechts für Erinnerungen zur Uhrzeit: „Follow-up fällig · 16:00“, „Anruf fällig“, „Verpasster Anruf“,
// „Neue SMS“, „Neue Voicemail“. Je Karte: Öffnen (bzw. Rückruf), Erledigt, Später (In 15 Minuten, 1 Stunde, Morgen 9 Uhr)
// und ✕ (gesehen, bleibt in der Inbox). Dazu eine Desktop-Mitteilung, wenn das Fenster nicht im Vordergrund steht
// (Electron-App: Systemmitteilung mit Klick zum Lead; Browser: Notification nach Erlaubnis). Holt alle 30 Sekunden und bei
// jeder Aufgaben-Änderung nach, quittiert wird auf dem Server (`POST /api/tasks/erinnerungen/{id}/gesehen`).
// Neue Karten bleiben 20 Sekunden stehen (solange die Maus nicht darauf liegt) und klappen dann ein, damit sie keine
// Zeilenaktionen und Symbole verdecken; die Zahl bleibt an der Glocke in der Kopfzeile, ein Klick darauf klappt sie wieder auf.
// 19.10.2026 (Simon, wie Close): Aufgaben mit Uhrzeit melden sich 5 Minuten vorher (Server) als Banner OBEN mit Lead-Name
// (Klick öffnet den Lead), Erledigt, Später (10 Min.) und Schließen; das Banner bleibt stehen, bis man etwas davon klickt.
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { aufgabenApi } from "../api";
import { useEchtzeit } from "../api/echtzeit";
import { inboxApi, type Erinnerung } from "../api/inbox";
import { EINKLAPPEN_NACH_MS, erinnerungAnzahlSetzen, erinnerungOffenSetzen, useErinnerungStand } from "../daten/erinnerungen";
import { useNeuVerbunden } from "../daten/verbindung";
import { berlinTeile, berlinZuIso, gueltig, heute, tagPlus } from "../werkzeuge/zeit";
import { Icon } from "./Icon";
import { useMeldung } from "./Meldungen";
import { MenueKnopf } from "./Menue";
import { TypSymbol, ART_LABEL } from "./inbox/TypKreis";
import { rueckrufMerken } from "./inbox/rueckruf";
import { istSystem, mitArt, type AufgabeArt } from "./inbox/typen";
import { useTelefonie } from "../telefonie";
import { telefonText } from "../werkzeuge/telefon";
import "./erinnerung.css";

const TAKT_MS = 30_000;
const SICHTBAR = 3;

const TITEL: Record<AufgabeArt, string> = {
  aufgabe: "Follow-up fällig",
  anruf: "Anruf fällig",
  verpasst: "Verpasster Anruf",
  angenommen: "Angenommener Anruf",
  voicemail: "Neue Voicemail",
  sms: "Neue SMS",
  opportunity: "Opportunity fällig",
};

const schluessel = (e: Erinnerung) => `${e.id}|${e.faellig_um}`;

export function uhrzeit(e: Erinnerung): string | null {
  return e.date && e.date.length > 10 && gueltig(e.date) ? berlinTeile(e.date).zeit : null;
}

export function titel(e: Erinnerung): string {
  const art = mitArt(e).art ?? "aufgabe";
  const zeit = art === "aufgabe" || art === "anruf" ? uhrzeit(e) : null;
  return `${TITEL[art]}${zeit ? ` · ${zeit}` : ""}`;
}

/** Aufgabe mit Uhrzeit (Follow-up, Anrufaufgabe): steht als Banner oben */
export function istZeitErinnerung(e: Erinnerung): boolean {
  const art = mitArt(e).art ?? "aufgabe";
  return (art === "aufgabe" || art === "anruf") && !!uhrzeit(e);
}

/** „in 4 Min.“ bzw. „jetzt“ bis zur Uhrzeit der Aufgabe */
export function bisDahin(e: Erinnerung, jetzt = Date.now()): string {
  const ziel = new Date(e.faellig_um || e.date || "").getTime();
  if (!Number.isFinite(ziel)) return "";
  const min = Math.ceil((ziel - jetzt) / 60_000);
  return min > 0 ? `in ${min} Min.` : "jetzt";
}

export function zeile1(e: Erinnerung): string {
  const x = mitArt(e);
  return e.lead_name ? `${e.lead_name}${e.contact_name && e.contact_name !== e.lead_name ? `, ${e.contact_name}` : ""}` : (x.phone ? telefonText(x.phone, x.phone_formatted) : "Unbekannte Nummer");
}

export function ErinnerungBanner() {
  const navigate = useNavigate();
  const meldung = useMeldung();
  const tel = useTelefonie();
  const [liste, setListe] = useState<Erinnerung[]>([]);
  const [weg, setWeg] = useState<Set<string>>(new Set());
  const [beschaeftigt, setBeschaeftigt] = useState<string | null>(null);
  const [erlaubnis, setErlaubnis] = useState<NotificationPermission | "keine">(() => (typeof Notification === "undefined" ? "keine" : Notification.permission));
  const bekannt = useRef<Set<string> | null>(null); // beim ersten Laden schon vorhanden: kein Systemhinweis dafür
  const zeit = useRef<number | undefined>(undefined);
  const laeuft = useRef(false);
  const { offen } = useErinnerungStand();
  const [ueber, setUeber] = useState(false); // Maus oder Tastaturfokus auf den Karten: nicht einklappen
  const angekuendigt = useRef<Set<string>>(new Set());

  const hinweisen = useCallback(
    (e: Erinnerung) => {
      if (document.hasFocus() && document.visibilityState === "visible") return; // das Banner ist zu sehen
      const art = mitArt(e).art ?? "aufgabe";
      const text = `${zeile1(e)}${art === "aufgabe" || art === "anruf" || art === "sms" ? `: ${mitArt(e).anzeige_text ?? e.text ?? ""}` : ""}`.slice(0, 160);
      const ziel = e.lead_id ? `/lead/${encodeURIComponent(e.lead_id)}` : "/inbox?reiter=calls";
      const kopf = titel(e);
      if (window.crmDesktop) {
        void Promise.resolve(window.crmDesktop.benachrichtigen(kopf, text, ziel)).catch(() => undefined);
      } else if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        try {
          const n = new Notification(kopf, { body: text, tag: schluessel(e) });
          n.onclick = () => {
            window.focus();
            navigate(ziel);
            n.close();
          };
        } catch {
          /* manche Umgebungen erlauben keine Notification, das Banner bleibt */
        }
      }
    },
    [navigate],
  );

  const laden = useCallback(async () => {
    if (laeuft.current) return;
    laeuft.current = true;
    try {
      const r = await inboxApi.erinnerungen();
      setListe(r.data);
      const jetzt = new Set(r.data.map(schluessel));
      if (bekannt.current === null) {
        bekannt.current = jetzt;
      } else {
        for (const e of r.data) {
          if (!bekannt.current.has(schluessel(e))) hinweisen(e);
        }
        r.data.forEach((e) => bekannt.current?.add(schluessel(e)));
      }
    } catch {
      /* ohne Verbindung oder ausgeloggt: beim nächsten Takt wieder */
    } finally {
      laeuft.current = false;
    }
  }, [hinweisen]);

  useEffect(() => {
    void laden();
    const t = window.setInterval(() => void laden(), TAKT_MS);
    const sichtbar = () => {
      if (document.visibilityState === "visible") void laden();
    };
    document.addEventListener("visibilitychange", sichtbar);
    return () => {
      window.clearInterval(t);
      document.removeEventListener("visibilitychange", sichtbar);
    };
  }, [laden]);

  useEchtzeit(["task.created", "task.updated"], () => {
    window.clearTimeout(zeit.current);
    zeit.current = window.setTimeout(() => void laden(), 600);
  });
  useNeuVerbunden(() => void laden());
  useEffect(() => () => window.clearTimeout(zeit.current), []);

  const entfernen = (e: Erinnerung) => setWeg((w) => new Set(w).add(schluessel(e)));

  const quittieren = async (e: Erinnerung) => {
    entfernen(e);
    try {
      await inboxApi.gesehen(e.id);
    } catch {
      /* nicht schlimm: beim nächsten Abruf kommt sie nur wieder, wenn das Speichern wirklich fehlte */
    }
  };

  const erledigen = async (e: Erinnerung) => {
    setBeschaeftigt(e.id);
    try {
      await aufgabenApi.erledigen(e.id);
      entfernen(e);
      meldung.ok("Erledigt.");
    } catch (err) {
      meldung.fehler(err, "Nicht erledigt");
    } finally {
      setBeschaeftigt(null);
    }
  };

  const spaeter = async (e: Erinnerung, date: string, text: string) => {
    setBeschaeftigt(e.id);
    try {
      await aufgabenApi.aendern(e.id, { date });
      entfernen(e);
      meldung.ok(`Erinnerung ${text}.`);
    } catch (err) {
      meldung.fehler(err, "Nicht verschoben");
    } finally {
      setBeschaeftigt(null);
    }
  };

  // „Später“ im Banner oben: der Server blendet sie 10 Minuten aus; danach darf sie hier wieder erscheinen (und meldet sich neu)
  const spaeterKurz = async (e: Erinnerung) => {
    const k = schluessel(e);
    entfernen(e);
    try {
      await inboxApi.spaeter(e.id);
      meldung.ok("Erinnerung in 10 Minuten.");
    } catch (err) {
      meldung.fehler(err, "Nicht verschoben");
    }
    window.setTimeout(() => {
      bekannt.current?.delete(k);
      setWeg((w) => {
        const n = new Set(w);
        n.delete(k);
        return n;
      });
      void laden();
    }, 10 * 60_000 + 5_000);
  };

  const leadOeffnen = (e: Erinnerung) => {
    navigate(e.lead_id ? `/lead/${encodeURIComponent(e.lead_id)}` : "/inbox");
  };

  const oeffnen = (e: Erinnerung) => {
    void quittieren(e);
    navigate(e.lead_id ? `/lead/${encodeURIComponent(e.lead_id)}` : "/inbox?reiter=calls");
  };

  const rueckruf = (e: Erinnerung) => {
    const x = mitArt(e);
    if (!x.phone || !e.lead_id) return oeffnen(e);
    void quittieren(e);
    if (x.art !== "anruf") rueckrufMerken(e.id, e.lead_id);
    void tel.anrufen(x.phone, e.lead_id, e.contact_id ?? undefined, { leadName: e.lead_name, kontaktName: e.contact_name });
  };

  const sichtbar = liste.filter((e) => !weg.has(schluessel(e)));
  const sichtbarSchluessel = sichtbar.map(schluessel).join(",");

  // Zahl an die Glocke melden; eine neue Karte klappt alles auf
  useEffect(() => {
    erinnerungAnzahlSetzen(sichtbar.length);
    const neu = sichtbar.map(schluessel).filter((k) => !angekuendigt.current.has(k));
    if (neu.length) {
      neu.forEach((k) => angekuendigt.current.add(k));
      erinnerungOffenSetzen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sichtbarSchluessel]);
  useEffect(() => () => erinnerungAnzahlSetzen(0), []);

  // nach einer Weile einklappen, solange nichts angefasst wird
  useEffect(() => {
    if (!offen || ueber || !sichtbar.length) return;
    const t = window.setTimeout(() => erinnerungOffenSetzen(false), EINKLAPPEN_NACH_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offen, ueber, sichtbarSchluessel]);

  const oben = sichtbar.filter(istZeitErinnerung);
  const unten = sichtbar.filter((e) => !istZeitErinnerung(e));
  const gezeigt = unten.slice(0, SICHTBAR);
  const rest = unten.length - gezeigt.length;

  const banner = oben.length ? (
    <div className="erinnerung-oben" role="alert" aria-label="Erinnerung an Aufgaben">
      {oben.slice(0, SICHTBAR).map((e) => {
        const x = mitArt(e);
        const art = x.art ?? "aufgabe";
        return (
          <div key={schluessel(e)} className={`erinnerung-oben__zeile erinnerung-oben__zeile--${art}`} data-erinnerung-oben={e.id}>
            <Icon name="uhr" groesse={14} />
            <span className="erinnerung-oben__titel">
              {titel(e)} <span className="erinnerung-oben__bis">({bisDahin(e)})</span>
            </span>
            {e.lead_id ? (
              <button type="button" className="erinnerung-oben__lead" title="Lead öffnen" onClick={() => leadOeffnen(e)}>
                {zeile1(e)}
              </button>
            ) : (
              <span className="erinnerung-oben__lead">{zeile1(e)}</span>
            )}
            {x.anzeige_text ?? e.text ? <span className="erinnerung-oben__text">{x.anzeige_text ?? e.text}</span> : null}
            <span className="erinnerung-oben__aktionen">
              {art === "anruf" && x.phone && e.lead_id ? (
                <button type="button" className="knopf knopf--klein knopf--primaer" onClick={() => rueckruf(e)}>
                  <Icon name="telefon" groesse={13} /> Anrufen
                </button>
              ) : null}
              <button type="button" className="knopf knopf--klein" disabled={beschaeftigt === e.id} onClick={() => void erledigen(e)}>
                <Icon name="haken" groesse={13} /> Erledigt
              </button>
              <button type="button" className="knopf knopf--klein" onClick={() => void spaeterKurz(e)}>
                <Icon name="uhr" groesse={13} /> Später (10 Min.)
              </button>
              <button type="button" className="symbolknopf symbolknopf--klein" title="Schließen (bleibt in der Inbox)" aria-label="Schließen" onClick={() => void quittieren(e)}>
                <Icon name="schliessen" groesse={13} />
              </button>
            </span>
          </div>
        );
      })}
    </div>
  ) : null;

  if (!unten.length || !offen) return banner;

  return (
    <>
    {banner}
    <div
      className="erinnerungen"
      role="region"
      aria-label="Erinnerungen"
      aria-live="polite"
      onMouseEnter={() => setUeber(true)}
      onMouseLeave={() => setUeber(false)}
      onFocus={() => setUeber(true)}
      onBlur={(ev) => {
        if (!ev.currentTarget.contains(ev.relatedTarget as Node | null)) setUeber(false);
      }}
    >
      {gezeigt.map((e, i) => {
        const x = mitArt(e);
        const art = x.art ?? "aufgabe";
        const system = istSystem(e);
        const kannRueckruf = !!x.phone && !!e.lead_id && (art === "verpasst" || art === "voicemail" || art === "angenommen" || art === "anruf");
        const morgen = berlinZuIso(tagPlus(heute(), 1), "09:00");
        return (
          <div key={schluessel(e)} className={`erinnerung erinnerung--${art}`}>
            <div className="erinnerung__kopf">
              <span className={`erinnerung__kreis aufgabe__haken--art-${art}`} title={ART_LABEL[art]}>
                <TypSymbol art={art} erledigt={false} />
              </span>
              <span className="erinnerung__titel">{titel(e)}</span>
              <button type="button" className="symbolknopf symbolknopf--klein" title="Ausblenden (bleibt in der Inbox)" aria-label="Ausblenden" onClick={() => void quittieren(e)}>
                <Icon name="schliessen" groesse={13} />
              </button>
            </div>
            <div className="erinnerung__lead" title={zeile1(e)}>
              {zeile1(e)}
            </div>
            {art === "aufgabe" || art === "anruf" || art === "sms" ? <div className="erinnerung__text">{x.anzeige_text ?? e.text}</div> : null}
            <div className="erinnerung__aktionen">
              {kannRueckruf ? (
                <button type="button" className="knopf knopf--klein knopf--primaer" onClick={() => rueckruf(e)} title={`${art === "anruf" ? "Anrufen" : "Zurückrufen"}: ${telefonText(x.phone, x.phone_formatted)}`}>
                  <Icon name="telefon" groesse={13} /> {art === "anruf" ? "Anrufen" : "Rückruf"}
                </button>
              ) : (
                <button type="button" className="knopf knopf--klein knopf--primaer" onClick={() => oeffnen(e)}>
                  Öffnen
                </button>
              )}
              {kannRueckruf ? (
                <button type="button" className="knopf knopf--klein" onClick={() => oeffnen(e)}>
                  Öffnen
                </button>
              ) : null}
              <button type="button" className="knopf knopf--klein" disabled={beschaeftigt === e.id} onClick={() => void erledigen(e)}>
                <Icon name="haken" groesse={13} /> Erledigt
              </button>
              {!system ? (
                <MenueKnopf
                  knopfKlasse="knopf knopf--klein"
                  titel="Später erinnern"
                  ausrichtung="start"
                  deaktiviert={beschaeftigt === e.id}
                  punkte={[
                    { label: "In 15 Minuten", icon: "uhr", onClick: () => void spaeter(e, new Date(Date.now() + 15 * 60_000).toISOString(), "in 15 Minuten") },
                    { label: "In 1 Stunde", icon: "uhr", onClick: () => void spaeter(e, new Date(Date.now() + 60 * 60_000).toISOString(), "in 1 Stunde") },
                    { label: "Morgen, 9:00 Uhr", icon: "kalender", onClick: () => void spaeter(e, morgen, "morgen um 9:00 Uhr") },
                  ]}
                >
                  <Icon name="uhr" groesse={13} /> Später
                </MenueKnopf>
              ) : null}
            </div>
            {i === 0 && erlaubnis === "default" && !window.crmDesktop ? (
              <button
                type="button"
                className="erinnerung__erlauben"
                title="Dann meldet sich die Erinnerung auch, wenn dieses Fenster hinten liegt."
                onClick={() => {
                  void Notification.requestPermission().then((p) => setErlaubnis(p));
                }}
              >
                Desktop-Mitteilungen erlauben
              </button>
            ) : null}
          </div>
        );
      })}
      {rest > 0 ? (
        <button type="button" className="erinnerung__weitere" onClick={() => navigate("/inbox")}>
          + {rest} weitere in der Inbox
        </button>
      ) : null}
    </div>
    </>
  );
}
